import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { LicensePlateRecognitionService, LicensePlateRecognitionDto, PointDirection } from '../../api/gen';
import { environment } from '../../environments/environment';

export type ExportStatus = 'idle' | 'fetching-metadata' | 'downloading-images' | 'building-zip' | 'done' | 'error';

export interface ExportParams {
    cameraIds: string[];
    startDate: string;
    endDate: string;
    plateNumber?: string;
    direction?: PointDirection;
    includeImages: boolean;
}

@Injectable({ providedIn: 'root' })
export class ExportService {
    private lprService = inject(LicensePlateRecognitionService);
    private http = inject(HttpClient);

    status = signal<ExportStatus>('idle');
    totalImages = signal(0);
    downloadedImages = signal(0);
    totalRecords = signal(0);

    private readonly PAGE_SIZE = 100;
    private readonly IMAGE_BATCH_SIZE = 5;
    private readonly MAX_RECORDS = 5000;

    /** In dev, rewrite blob storage URLs to go through the Angular proxy to avoid CORS. */
    private proxiedUrl(url: string): string {
        if (environment.production || environment.environment === 'testing') return url;
        try {
            const parsed = new URL(url);
            if (parsed.hostname.endsWith('.blob.core.windows.net')) {
                return `/blob-proxy${parsed.pathname}${parsed.search}`;
            }
        } catch { /* not a valid URL, return as-is */ }
        return url;
    }

    /** Convert a datetime-local string (local time, no offset) to a UTC ISO string for the API. */
    private toUtcIso(localDatetime: string): string {
        return new Date(localDatetime).toISOString();
    }

    /** Fetch a count-only preview (pageSize=1) for all selected cameras combined. */
    async getPreviewCount(params: Omit<ExportParams, 'includeImages'>): Promise<number> {
        let total = 0;
        for (const cameraId of params.cameraIds) {
            const result = await firstValueFrom(
                this.lprService.licensePlateRecognitionGet(
                    params.plateNumber,
                    cameraId,
                    this.toUtcIso(params.startDate),
                    this.toUtcIso(params.endDate),
                    params.direction,
                    undefined,
                    0,
                    1
                )
            );
            total += result.totalCount ?? 0;
        }
        return total;
    }

    async export(params: ExportParams): Promise<void> {
        try {
            this.status.set('fetching-metadata');
            this.downloadedImages.set(0);
            this.totalImages.set(0);

            const allRecords = await this.fetchAllRecords(params);
            this.totalRecords.set(allRecords.length);

            if (!params.includeImages) {
                const csv = this.buildCsv(allRecords);
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                saveAs(blob, `detections_${this.fileTimestamp()}.csv`);
                this.status.set('done');
                return;
            }

            this.status.set('downloading-images');
            const zip = new JSZip();
            zip.file('metadata.csv', this.buildCsv(allRecords));

            const imageCount = allRecords.reduce((n, r) => n + (r.vehicleImageUrl ? 1 : 0) + (r.plateImageUrl ? 1 : 0), 0);
            this.totalImages.set(imageCount);

            const imagesFolder = zip.folder('images')!;
            await this.downloadImagesInBatches(allRecords, imagesFolder);

            this.status.set('building-zip');
            const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
            saveAs(zipBlob, `detections_${this.fileTimestamp()}.zip`);
            this.status.set('done');
        } catch (err) {
            console.error('Export failed', err);
            this.status.set('error');
        }
    }

    private async fetchAllRecords(params: ExportParams): Promise<LicensePlateRecognitionDto[]> {
        const all: LicensePlateRecognitionDto[] = [];

        for (const cameraId of params.cameraIds) {
            let pageIndex = 0;
            let fetched = 0;
            let total = 1; // init > 0 to enter loop

            while (fetched < total && all.length < this.MAX_RECORDS) {
                const result = await firstValueFrom(
                    this.lprService.licensePlateRecognitionGet(
                        params.plateNumber,
                        cameraId,
                        this.toUtcIso(params.startDate),
                        this.toUtcIso(params.endDate),
                        params.direction,
                        undefined,
                        pageIndex,
                        this.PAGE_SIZE
                    )
                );
                total = result.totalCount ?? 0;
                const items = result.items ?? [];
                all.push(...items);
                fetched += items.length;
                pageIndex++;
                if (items.length === 0) break;
            }
        }

        return all.slice(0, this.MAX_RECORDS);
    }

    private async downloadImagesInBatches(
        records: LicensePlateRecognitionDto[],
        folder: JSZip
    ): Promise<void> {
        type ImageTask = { id: string; url: string; filename: string };
        const tasks: ImageTask[] = [];

        for (const r of records) {
            if (!r.id) continue;
            if (r.vehicleImageUrl) tasks.push({ id: r.id, url: this.proxiedUrl(r.vehicleImageUrl), filename: `${r.id}_vehicle.jpg` });
            if (r.plateImageUrl) tasks.push({ id: r.id, url: this.proxiedUrl(r.plateImageUrl), filename: `${r.id}_plate.jpg` });
        }

        for (let i = 0; i < tasks.length; i += this.IMAGE_BATCH_SIZE) {
            const batch = tasks.slice(i, i + this.IMAGE_BATCH_SIZE);
            await Promise.all(
                batch.map(async (task) => {
                    try {
                        const blob = await firstValueFrom(this.http.get(task.url, { responseType: 'blob' }));
                        folder.file(task.filename, blob);
                    } catch {
                        // skip images that fail to download
                    } finally {
                        this.downloadedImages.update(n => n + 1);
                    }
                })
            );
        }
    }

    private buildCsv(records: LicensePlateRecognitionDto[]): string {
        const headers = [
            'id', 'plateNumber', 'confidence', 'detectionTime', 'cameraId',
            'direction', 'plateColor', 'plateType', 'countryCode', 'regionCode',
            'lane', 'speed',
        ];
        const escape = (v: unknown): string => {
            if (v == null) return '';
            const s = String(v);
            return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const rows = records.map(r => [
            r.id, r.plateNumber, r.confidence, r.detectionTime, r.cameraId,
            r.direction, r.plateColor, r.plateType, r.countryCode, r.regionCode,
            r.lane, r.speed,
        ].map(escape).join(','));
        return [headers.join(','), ...rows].join('\r\n');
    }

    private fileTimestamp(): string {
        return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    }

    resetState(): void {
        this.status.set('idle');
        this.totalImages.set(0);
        this.downloadedImages.set(0);
        this.totalRecords.set(0);
    }
}
